import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO_LINES, HERO_HIGHLIGHT, HERO_SUB, METRICS, DECK } from "@/data/content";
import { FileText, ArrowUpRight, Download } from "lucide-react";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.3 + i * 0.12, ease: [0.76, 0, 0.24, 1] },
  }),
};

function DeckCard({ y }) {
  return (
    <motion.div style={{ y }} className="relative w-full max-w-md mx-auto">
      <motion.div
        initial={{ opacity: 0, rotateX: 12, y: 40 }}
        animate={{ opacity: 1, rotateX: 0, y: 0 }}
        transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
      >
        {/* ambient glow */}
        <div className="absolute -inset-6 rounded-[28px] bg-flame/20 blur-3xl opacity-70" />

        {/* deck cover card */}
        <div className="relative rounded-2xl overflow-hidden border border-flame/40 shadow-[0_30px_80px_-20px_rgba(247,115,45,0.35)]">
          <img
            src={DECK.cover}
            alt="U-TOPIA, Hold Digital Money investor deck cover"
            width="900"
            height="506"
            className="block w-full h-auto"
            decoding="async"
            fetchPriority="high"
          />

          {/* Top ribbon */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-navy/80 to-transparent">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-flame/60 bg-navy/70 backdrop-blur px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-widest text-flame">
              <FileText className="h-3 w-3" />
              Investor Deck
            </span>
            <span className="font-mono text-[0.6rem] uppercase tracking-widest text-white/70">
              {DECK.pages} pages · PDF
            </span>
          </div>
        </div>

        {/* CTA rail */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <a
            data-testid="deck-open-btn"
            href={DECK.file}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between gap-2 rounded-xl bg-flame px-4 py-3.5 font-display font-bold text-navy transition-all duration-300 hover:bg-ember"
          >
            <span className="text-sm">Open Deck</span>
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            data-testid="deck-download-btn"
            href={DECK.file}
            download="U-TOPIA-Investor-Deck.pdf"
            className="group flex items-center justify-between gap-2 rounded-xl border border-white/15 bg-white/[0.03] px-4 py-3.5 font-display font-bold text-white transition-all duration-300 hover:border-flame/50 hover:bg-white/[0.06]"
          >
            <span className="text-sm">Download</span>
            <Download className="h-4 w-4 text-flame transition-transform duration-300 group-hover:translate-y-0.5" />
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      id="thesis"
      className="relative min-h-screen flex flex-col justify-center pt-10 pb-10 overflow-hidden"
    >
      <div className="absolute top-1/4 -left-40 h-[600px] w-[600px] rounded-full bg-flame/[0.07] blur-[80px] pointer-events-none" />

      <div className="mx-auto max-w-7xl px-6 w-full grid lg:grid-cols-[1.35fr_1fr] gap-12 lg:gap-14 items-center">
        <motion.div style={{ y: textY }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="inline-flex items-center gap-2 mb-8 rounded-full border border-white/10 bg-white/5 px-4 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-flame animate-pulse" />
            <span className="font-mono text-[0.68rem] tracking-[0.22em] uppercase text-white/60">
              Seed Round · Regulation D
            </span>
          </motion.div>

          <h1 className="font-display font-black tracking-[-0.02em] text-white text-4xl sm:text-6xl lg:text-[4.5rem] leading-[1.02]">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className="block"
                >
                  {line.includes(HERO_HIGHLIGHT) ? (
                    <>
                      {line.split(HERO_HIGHLIGHT)[0]}
                      <span className="text-flame">{HERO_HIGHLIGHT}</span>
                      {line.split(HERO_HIGHLIGHT)[1]}
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 max-w-xl text-white/60 text-base sm:text-lg leading-relaxed font-body"
          >
            {HERO_SUB}
          </motion.p>

          {/* Mobile deck CTA (visible when the desktop card is hidden) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-8 flex lg:hidden gap-3"
          >
            <a
              href={DECK.file}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-flame px-4 py-3 font-display font-bold text-navy text-sm"
            >
              <FileText className="h-4 w-4" /> View Deck
            </a>
            <a
              href={DECK.file}
              download="U-TOPIA-Investor-Deck.pdf"
              className="flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.03] px-4 py-3 text-white text-sm"
            >
              <Download className="h-4 w-4 text-flame" />
            </a>
          </motion.div>
        </motion.div>

        <div className="hidden lg:block" style={{ perspective: 1200 }}>
          <DeckCard y={cardY} />
        </div>
      </div>

      {/* Metrics strip */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3, duration: 0.9 }}
        className="mx-auto max-w-7xl px-6 w-full mt-16 lg:mt-24"
      >
        <div className="grid grid-cols-2 md:grid-cols-5 border-t border-white/10">
          {METRICS.map((m, i) => (
            <div
              key={i}
              data-testid={`metric-${i}`}
              className="border-b md:border-b-0 border-r border-white/10 last:border-r-0 py-5 px-4 group hover:bg-white/[0.02] transition-colors duration-300"
            >
              <div className="font-mono text-[0.62rem] tracking-[0.2em] uppercase text-white/40 mb-2">
                {m.k}
              </div>
              <div className="font-display font-bold text-white text-sm sm:text-base group-hover:text-flame transition-colors duration-300">
                {m.v}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
